<template>
  <div :class="rootClasses">
    <div v-if="showTitle || showSearch" class="flex flex-col gap-2.5">
      <p
        v-if="showTitle"
        class="font-helvetica-700 font-bold text-fluid-label text-gray-dark"
      >
        {{ title }}
      </p>
      <div
        v-if="showSearch"
        class="flex gap-3 rounded-[5px] bg-white-gray px-4 py-2.5"
      >
        <img src="/search.svg" />
        <input
          v-model="searchValue"
          type="text"
          placeholder="Поиск по складам"
          class="min-w-0 flex-1 font-helvetica-400 font-normal text-[15px] text-extra-text-color xl:text-[16px]"
        />
      </div>
    </div>
    <div
      class="flex flex-col gap-2.5"
      :class="isExpanded ? 'availability-scroll brand-scroll' : ''"
    >
      <div
        v-for="warehouse in visibleWarehouses"
        :key="warehouse.id"
        :class="getRowClasses(warehouse)"
      >
        <div :class="getWarehouseContentClasses(warehouse)">
          <AdaptiveFitText
            as="p"
            :text="warehouse.name"
            :tooltip="warehouse.name"
            :max-font-size="18"
            :min-font-size="12"
            :max-lines="3"
            :class="getWarehouseNameClasses(warehouse)"
          />
          <WarehouseStockMetrics
            :available="warehouse.stock"
            :reserve="warehouse.reserve"
            size="compact"
            :class="getWarehouseMetricsClasses(warehouse)"
          />
        </div>
        <div
          v-if="showWarehouseActions(warehouse)"
          :class="actionContainerClasses"
        >
          <div
            class="flex h-[36px] items-center gap-1 rounded-[10px] border border-extra-text-color px-1.5 py-1 sm:gap-1.5 sm:px-2 xl:gap-2 xl:px-2.5"
          >
            <button
              type="button"
              :disabled="isMinusDisabled(warehouse)"
              @click="emit('change-qty', warehouse, -1)"
              aria-label="Уменьшить"
              class="flex h-7 w-7 items-center justify-center rounded-[7px] text-extra-text-color transition-colors hover:bg-white-gray disabled:cursor-not-allowed disabled:opacity-45"
            >
              <span class="block h-[2px] w-[12px] bg-extra-text-color"></span>
            </button>
            <span
              class="w-[22px] text-center text-[13px] leading-[1.3] font-geologica font-normal text-gray-dark tabular-nums"
            >
              {{ warehouse.qty }}
            </span>
            <button
              type="button"
              class="flex h-7 w-7 items-center justify-center rounded-[7px] text-gray-light transition-colors cursor-pointer hover:bg-white-gray hover:text-orange disabled:cursor-not-allowed disabled:text-disabled-plus disabled:opacity-100"
              :disabled="isPlusDisabled(warehouse)"
              @click="emit('change-qty', warehouse, 1)"
              aria-label="Увеличить"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="text-extra-text-color"
              >
                <path d="M1 7H13M7 1V13" />
              </svg>
            </button>
          </div>
          <button
            type="button"
            class="h-[36px] min-w-[108px] cursor-pointer text-nowrap rounded-[10px] border px-3 text-[13px] font-helvetica-400 font-normal xl:min-w-[112px] xl:px-3.5 xl:text-[14px]"
            :class="
              warehouse.inCart
                ? 'border-green-button bg-green-button text-white hover:bg-green-button hover:border-green-button'
                : 'border-gray-dark bg-white text-gray-dark hover:border-orange hover:bg-orange hover:text-white'
            "
            :disabled="isCartUpdating(warehouse)"
            :aria-pressed="warehouse.inCart"
            @click="emit('add-to-cart', warehouse)"
          >
            {{ warehouse.inCart ? "В корзине" : "В корзину" }}
          </button>
        </div>
      </div>
      <div
        v-if="ghostWarehouse"
        :class="[getRowClasses(ghostWarehouse), 'opacity-40']"
      >
        <div :class="getWarehouseContentClasses(ghostWarehouse)">
          <AdaptiveFitText
            as="p"
            :text="ghostWarehouse.name"
            :tooltip="ghostWarehouse.name"
            :max-font-size="18"
            :min-font-size="12"
            :max-lines="3"
            :class="getWarehouseNameClasses(ghostWarehouse)"
          />
          <WarehouseStockMetrics
            :available="ghostWarehouse.stock"
            :reserve="ghostWarehouse.reserve"
            size="compact"
            :class="getWarehouseMetricsClasses(ghostWarehouse)"
          />
        </div>
        <div
          v-if="showWarehouseActions(ghostWarehouse)"
          :class="ghostActionContainerClasses"
          aria-hidden="true"
        >
          <div
            class="flex h-[36px] items-center gap-1 rounded-[10px] border border-extra-text-color px-1.5 py-1 sm:gap-1.5 sm:px-2 xl:gap-2 xl:px-2.5"
          >
            <span class="block h-[2px] w-[14px]"></span>
            <span
              class="w-[22px] text-center text-[13px] leading-[1.3] font-geologica font-normal text-gray-dark tabular-nums"
            >
              {{ ghostWarehouse.qty }}
            </span>
            <span class="block h-[14px] w-[14px]"></span>
          </div>
          <div
            class="flex h-[36px] min-w-[108px] items-center justify-center text-nowrap rounded-[10px] border px-3 font-helvetica-400 text-[13px] font-normal xl:min-w-[112px] xl:px-3.5 xl:text-[14px]"
          >
            {{ ghostWarehouse.inCart ? "В корзине" : "В корзину" }}
          </div>
        </div>
      </div>
    </div>
    <p v-if="!warehouses.length" class="text-[14px] text-gray-light">
      Склады не найдены
    </p>
    <button
      v-if="canToggle"
      type="button"
      class="mt-1 inline-flex items-center gap-2 text-[14px] font-helvetica-400 text-orange cursor-pointer"
      :aria-expanded="isExpanded"
      @click="isExpanded = !isExpanded"
    >
      <span>{{
        isExpanded
          ? "Свернуть"
          : "Показать еще " + (props.warehouses.length - 3)
      }}</span>
      <svg
        class="h-3 w-3 transition-transform"
        :class="isExpanded ? '' : 'rotate-180'"
        viewBox="0 0 12 12"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M3 7l3-3 3 3" />
      </svg>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import AdaptiveFitText from "~/components/AdaptiveFitText.vue";
import WarehouseStockMetrics from "~/components/goods-one/WarehouseStockMetrics.vue";
import { isTransitWarehouseName } from "~/utils/warehouses";

type Warehouse = {
  id: number | string;
  name: string;
  stock: number;
  reserve: number;
  qty: number;
  inCart?: boolean;
  salesBlocked?: boolean;
  inTransit?: boolean;
};

const props = withDefaults(
  defineProps<{
    search: string;
    warehouses?: Warehouse[];
    isMinusDisabled?: (warehouse: Warehouse) => boolean;
    isPlusDisabled?: (warehouse: Warehouse) => boolean;
    isCartUpdating?: (warehouse: Warehouse) => boolean;
    showTitle?: boolean;
    showSearch?: boolean;
    showActions?: boolean;
    title?: string;
    variant?: "panel" | "embedded";
  }>(),
  {
    warehouses: () => [],
    isMinusDisabled: () => false,
    isPlusDisabled: () => false,
    isCartUpdating: () => false,
    showTitle: true,
    showSearch: true,
    showActions: true,
    title: "Наличие",
    variant: "panel",
  },
);

const emit = defineEmits<{
  (e: "update:search", value: string): void;
  (e: "change-qty", warehouse: Warehouse, delta: number): void;
  (e: "add-to-cart", warehouse: Warehouse): void;
}>();

const isExpanded = ref(false);
const maxVisible = 3;

const rootClasses = computed(() =>
  props.variant === "embedded"
    ? "flex flex-col gap-2.5 w-full"
    : "flex flex-col h-full w-full rounded-[20px] border border-gray-border bg-white p-4 gap-5 lg:p-[30px] lg:gap-7.5",
);

const actionContainerClasses = computed(
  () =>
    "flex w-full items-center justify-start gap-2 sm:w-auto xl:shrink-0 xl:self-end xl:gap-2.5",
);

const ghostActionContainerClasses = computed(
  () => `${actionContainerClasses.value} invisible pointer-events-none`,
);

const searchValue = computed({
  get: () => props.search,
  set: (value: string) => emit("update:search", value),
});

const showWarehouseActions = (warehouse: Warehouse) =>
  props.showActions && !warehouse.salesBlocked && warehouse.stock > 0;

const isTransitWarehouse = (warehouse: Warehouse) =>
  Boolean(warehouse.inTransit) || isTransitWarehouseName(warehouse.name);

const getRowClasses = (warehouse: Warehouse) =>
  showWarehouseActions(warehouse)
    ? "availability-row flex w-full min-w-0 max-w-full flex-col items-start gap-2.5 xl:flex xl:w-fit xl:max-w-full xl:flex-row xl:items-end xl:gap-1.5 2xl:gap-2.5"
    : "availability-row flex w-full min-w-0 max-w-full flex-col gap-2.5";

const getWarehouseContentClasses = (warehouse: Warehouse) =>
  showWarehouseActions(warehouse)
    ? "w-full min-w-0 max-w-full overflow-hidden flex flex-col items-start gap-2 font-helvetica-400 font-normal text-fluid-label text-gray-dark text-left xl:w-[228px] xl:shrink-0 2xl:w-[248px]"
    : "w-full min-w-0 max-w-full overflow-hidden flex flex-col gap-2 font-helvetica-400 font-normal text-fluid-label text-gray-dark";

const getWarehouseNameClasses = (warehouse: Warehouse) => [
  "w-full max-w-full break-words text-left",
  isTransitWarehouse(warehouse) ? "font-helvetica-700 text-transit-orange" : "",
];

const getWarehouseMetricsClasses = (_warehouse: Warehouse) =>
  "w-full max-w-[208px]";

const canToggle = computed(() => props.warehouses.length > maxVisible);

const visibleWarehouses = computed(() =>
  isExpanded.value ? props.warehouses : props.warehouses.slice(0, maxVisible),
);

const ghostWarehouse = computed(() => {
  if (isExpanded.value || !canToggle.value) return null;
  return props.warehouses[maxVisible] || null;
});
</script>

<!-- <style scoped>
.availability-scroll {
  max-height: calc(
    (var(--availability-row-height) * 4) + (var(--availability-gap) * 3)
  );
  overflow-y: auto;
  padding-right: 6px;
  --availability-row-height: 46px;
  --availability-gap: 10px;
}

.availability-row {
  min-height: var(--availability-row-height, 46px);
}

.brand-scroll::-webkit-scrollbar {
  width: 8px;
}

.brand-scroll::-webkit-scrollbar-thumb {
  background-color: var(--color-gray-light);
}

.brand-scroll::-webkit-scrollbar-track {
  background-color: var(--color-gray-track);
}
</style> -->
